import { useState } from 'react';
import { api } from '../lib/api.ts';
import { LoginArt } from './LoginArt.tsx';

/**
 * 改密页。两种场景共用一个表单：
 *   · 强制 —— 首次登录（或密码被重置），没有退路，服务端把会话锁死在改密上；
 *   · 主动 —— 已登录用户从侧栏进来定期换密码，可取消返回。
 * 服务端规则一致：验当前密码、拒绝新旧相同、改密即撤销其他会话。
 */
export function ChangePassword({
  username,
  onDone,
  onCancel,
  heading = '首次登录，设置你的新密码',
  submitLabel = '设置并进入系统',
}: {
  username: string;
  onDone: () => void;
  onCancel?: () => void;
  heading?: string;
  submitLabel?: string;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    if (next !== again) {
      setError('两次输入的新密码不一致');
      return;
    }
    if (next === current) {
      setError('新密码不能与当前密码相同');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.changePassword({ currentPassword: current, newPassword: next });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <LoginArt />
      <form className="auth-card" onSubmit={(e) => void submit(e)}>
        <div className="auth-head">
          <span className="auth-brand">UP-LAB</span>
          <span className="auth-sub">{heading}</span>
        </div>

        <p className="auth-note">
          账号 <b>{username}</b>
          。改密后其他设备上的登录会话将全部失效，当前会话保留。
        </p>

        <label className="auth-field">
          <span>当前密码</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <label className="auth-field">
          <span>新密码</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            placeholder="至少 8 位"
            minLength={8}
            required
          />
        </label>
        <label className="auth-field">
          <span>确认新密码</span>
          <input
            type="password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>

        {error !== null && <div className="auth-error">{error}</div>}

        <button className="auth-submit" disabled={busy}>
          {busy ? '处理中…' : submitLabel}
        </button>

        {onCancel ? (
          <button
            type="button"
            className="auth-submit auth-cancel"
            onClick={onCancel}
          >
            取消，返回系统
          </button>
        ) : null}

        <p className="auth-note">密码只存 scrypt 哈希 —— 忘了就只能找管理员重置。</p>
      </form>
    </div>
  );
}
